import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";

const TINTS = ["#A58FB2", "#C99BB5", "#8FAFCB", "#BFA06A", "#A994CF", "#C9A0A8"];
const LINGER = 3600;

const overlap = (a, b) => a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
const pad = (r, p) => ({ left: r.left - p, top: r.top - p, right: r.right + p, bottom: r.bottom + p });

function Word({ word, gust, onPick, onDone }) {
  const [phase, setPhase] = useState("in");
  const [scatter, setScatter] = useState(null);
  const letters = [...word.p.emotion];
  const seeds = useMemo(() => letters.map(() => [Math.random(), Math.random(), Math.random()]), [letters.length]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const id = setTimeout(() => setPhase("out"), 1300 + letters.length * 70 + (word.long ? 14000 : LINGER));
    return () => clearTimeout(id);
  }, [letters.length, word.long]);
  useEffect(() => {
    if (!gust) return;
    const dx = word.x + word.w / 2 - gust.x, dy = word.y + word.h / 2 - gust.y;
    const d = Math.hypot(dx, dy) || 1;
    setScatter({ dx: dx / d, dy: dy / d, k: 0.6 + gust.s });
    const id = setTimeout(() => setScatter(null), 1900);
    return () => clearTimeout(id);
  }, [gust]); // eslint-disable-line react-hooks/exhaustive-deps
  const target = (i) => {
    if (phase === "out") return { opacity: 0, filter: "blur(10px)", y: -14 - (i % 3) * 7, x: i % 2 ? 6 : -5, rotate: 0 };
    if (!scatter) return { opacity: 0.72, filter: "blur(0px)", y: 0, x: 0, rotate: 0 };
    const [a, b, c] = seeds[i];
    const far = (70 + a * 110) * scatter.k;
    return { opacity: 0.22, filter: "blur(1.5px)", x: scatter.dx * far + (b - 0.5) * 50, y: scatter.dy * far - 30 - c * 60, rotate: (a - 0.5) * 120 };
  };
  const timing = (i) =>
    phase === "out"
      ? { duration: 1.7, delay: i * 0.09, ease: "easeIn" }
      : scatter
        ? { duration: 1.1, delay: i * 0.025, ease: [0.2, 0.8, 0.3, 1] }
        : { duration: scatter === null && phase === "in" ? 1.5 : 1.3, delay: i * 0.06, ease: "easeInOut" };
  return (
    <motion.button
      data-testid={`bubble-feeling-${word.p.slug}`}
      data-emotion={word.p.emotion}
      data-phase={phase}
      data-scattered={scatter ? "true" : "false"}
      aria-label={`Feel ${word.p.emotion}`}
      onClick={() => onPick(word.p)}
      initial={{ y: 6 }}
      animate={{ y: -12 }}
      transition={{ duration: 10, ease: "linear" }}
      className="font-hand absolute z-10 whitespace-nowrap leading-none transition-[filter] duration-500 hover:brightness-90 hover:saturate-150"
      style={{ left: word.x, top: word.y, fontSize: word.size, color: word.tint, padding: "0.25em 0.35em", textShadow: "0 0 14px rgba(255,255,255,0.95), 0 0 28px rgba(255,255,255,0.7)" }}
    >
      {letters.map((ch, i) => (
        <motion.span
          key={i}
          className="inline-block"
          initial={{ opacity: 0, filter: "blur(8px)", y: 6 }}
          animate={target(i)}
          transition={timing(i)}
          onAnimationComplete={() => phase === "out" && i === letters.length - 1 && onDone(word.key)}
        >
          {ch === " " ? "\u00a0" : ch}
        </motion.span>
      ))}
    </motion.button>
  );
}

export function FeelingWords({ paintings, avoidRef, onPick, gust }) {
  const [words, setWords] = useState([]);
  const live = useRef([]);
  live.current = words;

  const spawn = useCallback(
    (slug) => {
      const W = window.innerWidth, H = window.innerHeight;
      const mobile = W < 640;
      const shown = new Set(live.current.map((w) => w.p.slug));
      if (slug && shown.has(slug)) return true;
      const pool = paintings.filter((p) => (slug ? p.slug === slug : !shown.has(p.slug)));
      if (!pool.length) return false;
      const p = pool[Math.floor(Math.random() * pool.length)];
      const size = mobile ? 26 : 32 + Math.random() * 8;
      const w = p.emotion.length * size * 0.46 + size * 0.7, h = size * 1.5;
      const avoid = [...(avoidRef.current?.children || []), document.querySelector('[data-testid="breath-muse"]')].filter(Boolean).map((el) => pad(el.getBoundingClientRect(), 22));
      avoid.push({ left: W - 120, top: 0, right: W, bottom: 96 }, { left: W - 120, top: H - 50, right: W, bottom: H });
      const others = live.current.map((o) => pad({ left: o.x, top: o.y, right: o.x + o.w, bottom: o.y + o.h }, 26));
      for (let i = 0; i < 80; i++) {
        const x = 14 + Math.random() * Math.max(1, W - w - 28);
        const y = 64 + Math.random() * Math.max(1, H - h - 120);
        const r = { left: x, top: y, right: x + w, bottom: y + h };
        if (r.right > W - 8 || avoid.some((a) => overlap(a, r)) || others.some((a) => overlap(a, r))) continue;
        const word = { key: `${p.slug}-${performance.now()}`, p, x, y, w, h, size, tint: TINTS[Math.floor(Math.random() * TINTS.length)], long: !!slug };
        live.current = [...live.current, word];
        setWords(live.current);
        return true;
      }
      return false;
    },
    [paintings, avoidRef],
  );

  useEffect(() => {
    if (!paintings.length) return;
    const first = setTimeout(() => spawn(), 1800);
    const id = setInterval(() => {
      const max = window.innerWidth < 640 ? 2 : 3;
      if (live.current.length < max && Math.random() < 0.7) spawn();
    }, 1500);
    return () => {
      clearTimeout(first);
      clearInterval(id);
    };
  }, [paintings, spawn]);

  useEffect(() => {
    window.__afaeWords = {
      list: () => live.current.map((w) => ({ slug: w.p.slug, emotion: w.p.emotion, x: w.x, y: w.y, w: w.w, h: w.h })),
      show: (slug) => spawn(slug),
    };
    return () => delete window.__afaeWords;
  }, [spawn]);

  const done = (key) => setWords((l) => (live.current = l.filter((w) => w.key !== key)));
  return (
    <div data-testid="feeling-words" className="pointer-events-none absolute inset-0 z-10 [&>button]:pointer-events-auto">
      {words.map((w) => (
        <Word key={w.key} word={w} gust={gust} onPick={onPick} onDone={done} />
      ))}
    </div>
  );
}
